/**
 * ConvergenceSkillEffect — Conjuration Swarm Fork 2 command: rush all wisps
 * toward a single target position.
 *
 * On activation, all living wisps simultaneously surge toward the nearest
 * enemy (auto-target) at `rushSpeedMultiplier × driftSpeed`. Each wisp
 * deals `impactDamage` on arrival and is destroyed with deathCause 'converge'
 * — Hair Trigger and Expendable do NOT fire (they guard against 'converge').
 *
 * Upgrade hooks (resolved at activation time via UpgradeEffectResolver):
 * - Spearhead (convergence_upgrade_1): rushSpeedMultiplier max 4.5
 *   (replaces base 3.0, not additive); vanguardStaggerMs additive 300
 *   (first wisp to arrive staggers the target for 300ms)
 * - Cascade (convergence_upgrade_2): after commandConverge fires, starts a
 *   timed auto-spawn window. Every postConvergenceAutoSpawnInterval ms (min
 *   aggregation, 1500ms), if wispCount < maxWisps and autoSpawnCount <
 *   postConvergenceAutoSpawnMax (max, 2), spawn a wisp at the player's
 *   current position. Window lasts postConvergenceAutoSpawnDurationMs (max,
 *   4000ms). Only one window runs at a time — a second Convergence during an
 *   active window does not start a new overlapping wave.
 *
 * UpgradeEffectResolver keys (convergence scope):
 * - rushSpeedMultiplier: max aggregation, base 3.0 — Spearhead sets 4.5
 * - vanguardStaggerMs: additive, base 0 — Spearhead contributes 300
 * - postConvergenceAutoSpawnInterval: min aggregation — Cascade sets 1500
 * - postConvergenceAutoSpawnDurationMs: max aggregation — Cascade sets 4000
 * - postConvergenceAutoSpawnMax: max aggregation — Cascade sets 2
 *
 * Long cooldown tier (2000ms). Cooldown is consumed even when no wisps are
 * alive (the command fires but is a no-op). Cooldown is consumed even when
 * no enemy is in range (same reasoning). Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see conjuration-spec.md § Convergence, § Spearhead, § Cascade
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface ConvergenceDeps {
  readonly resolver: {
    /** Returns the max override value for a key, or undefined if not modified. */
    getMaxEffect(skillId: string, key: string): number | undefined;
    /** Returns the additive sum for a key across active upgrades. Returns 0 if absent. */
    getAdditiveEffect(skillId: string, key: string): number;
    /** Returns the minimum value for a key across active upgrades, or undefined if absent. */
    getMinEffect(skillId: string, key: string): number | undefined;
  };
  readonly summons: {
    /** Returns the number of currently living wisps. */
    getWispCount(): number;
    /** Returns the current wisp cap (maxWisps). Used by Cascade to gate auto-spawns. */
    getMaxWisps(): number;
    /**
     * Command all living wisps to converge on (targetX, targetY).
     * Wisps move at `rushSpeedMultiplier × driftSpeed` and are destroyed on
     * arrival with deathCause 'converge', dealing `impactDamage` on contact.
     * The first wisp to reach the target applies stagger for `vanguardStaggerMs`
     * if > 0 (Spearhead upgrade). Remaining wisps do not re-apply stagger.
     */
    commandConverge(
      targetX: number,
      targetY: number,
      rushSpeedMultiplier: number,
      impactDamage: number,
      vanguardStaggerMs: number,
    ): void;
    /**
     * Spawn a single replacement wisp at (x, y) with the standard Overwhelm-tier
     * wisp config. Used by Cascade auto-spawn. Returns the new wisp's ID.
     */
    spawnWisp(x: number, y: number): string;
  };
  readonly player: {
    /** Returns the player's current world position. Used by Cascade to place auto-spawned wisps. */
    getPosition(): { x: number; y: number };
  };
  readonly targeting: {
    /** Returns the nearest enemy position, or null if no enemies are present. */
    getNearestEnemy(): { x: number; y: number } | null;
  };
}

// ---------------------------------------------------------------------------
// Skill tuning
// ---------------------------------------------------------------------------

export interface ConvergenceConfig {
  readonly cooldownMs: number;
  /** Base damage dealt by each wisp on arrival at the convergence target. */
  readonly baseImpactDamage: number;
  /** Base rush speed multiplier (3×). Spearhead raises this to 4.5× via max. */
  readonly baseRushSpeedMultiplier: number;
}

const DEFAULT_CONFIG: ConvergenceConfig = {
  cooldownMs: 2000,              // long cooldown tier (matches Detonate)
  baseImpactDamage: 8,           // spec § Convergence: "same base as Detonate"
  baseRushSpeedMultiplier: 3.0,  // spec § Convergence: "3× drift speed"
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class ConvergenceSkillEffect implements SkillEffect {
  readonly skillId = 'convergence';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: ConvergenceDeps;
  private readonly config: ConvergenceConfig;

  private _cooldownRemaining = 0;

  // Observable state for scenarios and integration layer
  private _commandCount = 0;
  private _lastTargetX = 0;
  private _lastTargetY = 0;
  private _lastWispCount = 0;
  private _lastRushSpeedMultiplier = 0;
  private _lastVanguardStaggerMs = 0;
  private _noTargetCommandCount = 0;

  // Cascade (convergence_upgrade_2) auto-spawn timer state
  private _cascadeWindowRemaining = 0;   // ms remaining in active auto-spawn window
  private _cascadeIntervalRemaining = 0; // ms until the next auto-spawn attempt
  private _cascadeSpawnCount = 0;        // wisps auto-spawned in the current window
  private _cascadeMaxSpawns = 0;         // resolved postConvergenceAutoSpawnMax
  private _cascadeIntervalMs = 0;        // resolved postConvergenceAutoSpawnInterval
  private _totalCascadeSpawns = 0;       // cumulative cascade spawns (observable)

  constructor(deps: ConvergenceDeps, config: Partial<ConvergenceConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get commandCount(): number { return this._commandCount; }
  get lastTargetX(): number { return this._lastTargetX; }
  get lastTargetY(): number { return this._lastTargetY; }
  get lastWispCount(): number { return this._lastWispCount; }
  get lastRushSpeedMultiplier(): number { return this._lastRushSpeedMultiplier; }
  /** vanguardStaggerMs resolved at last activation (0 = Spearhead not active). */
  get lastVanguardStaggerMs(): number { return this._lastVanguardStaggerMs; }
  /** Number of activations where no enemy target was found. */
  get noTargetCommandCount(): number { return this._noTargetCommandCount; }
  /** True while a Cascade auto-spawn window is active. */
  get cascadeWindowActive(): boolean { return this._cascadeWindowRemaining > 0; }
  /** Wisps auto-spawned in the current (or most recently completed) Cascade window. */
  get cascadeSpawnCount(): number { return this._cascadeSpawnCount; }
  /** Cumulative Cascade auto-spawns across all activations. */
  get totalCascadeSpawns(): number { return this._totalCascadeSpawns; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Resolve rush speed (Spearhead raises to 4.5 via max aggregation)
    const rushOverride = this.deps.resolver.getMaxEffect('convergence', 'rushSpeedMultiplier');
    const rushSpeedMultiplier = rushOverride !== undefined
      ? Math.max(this.config.baseRushSpeedMultiplier, rushOverride)
      : this.config.baseRushSpeedMultiplier;

    // Resolve vanguard stagger duration (Spearhead contributes 300ms; 0 = inactive)
    const vanguardStaggerMs = this.deps.resolver.getAdditiveEffect('convergence', 'vanguardStaggerMs');

    const wispCount = this.deps.summons.getWispCount();
    const target = this.deps.targeting.getNearestEnemy();

    this._commandCount++;
    this._lastWispCount = wispCount;
    this._lastRushSpeedMultiplier = rushSpeedMultiplier;
    this._lastVanguardStaggerMs = vanguardStaggerMs;
    this._cooldownRemaining = this.config.cooldownMs;

    if (target === null) {
      // No enemy in range — cooldown consumed, command suppressed
      this._noTargetCommandCount++;
      return true;
    }

    this._lastTargetX = target.x;
    this._lastTargetY = target.y;

    // Issue convergence command to SummonSystem (no-op if wispCount === 0).
    // vanguardStaggerMs > 0 signals SummonSystem to apply stagger via the
    // first wisp to land (Spearhead upgrade behavior).
    this.deps.summons.commandConverge(
      target.x,
      target.y,
      rushSpeedMultiplier,
      this.config.baseImpactDamage,
      vanguardStaggerMs,
    );

    // Cascade (convergence_upgrade_2): start auto-spawn window if upgrade is
    // active and no window is already running. Only starts when commandConverge
    // fires (target present) — suppressed activations don't trigger Cascade.
    const cascadeIntervalMs = this.deps.resolver.getMinEffect(
      'convergence',
      'postConvergenceAutoSpawnInterval',
    );
    if (cascadeIntervalMs !== undefined && cascadeIntervalMs > 0 && this._cascadeWindowRemaining <= 0) {
      const durationMs =
        this.deps.resolver.getMaxEffect('convergence', 'postConvergenceAutoSpawnDurationMs') ?? 0;
      const maxSpawns =
        this.deps.resolver.getMaxEffect('convergence', 'postConvergenceAutoSpawnMax') ?? 0;
      if (durationMs > 0 && maxSpawns > 0) {
        this._cascadeWindowRemaining = durationMs;
        this._cascadeIntervalRemaining = cascadeIntervalMs;
        this._cascadeSpawnCount = 0;
        this._cascadeMaxSpawns = maxSpawns;
        this._cascadeIntervalMs = cascadeIntervalMs;
      }
    }

    return true;
  }

  deactivate(): void {
    // Instant skill — no toggle state
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }

    // Cascade auto-spawn timer (convergence_upgrade_2)
    if (this._cascadeWindowRemaining > 0) {
      this._cascadeWindowRemaining -= delta;
      this._cascadeIntervalRemaining -= delta;

      // Fire spawn attempts while interval has elapsed and window is still open.
      // Guard on cascadeWindowRemaining > 0 so a spawn doesn't slip through in
      // the same tick that the window expires.
      while (
        this._cascadeIntervalRemaining <= 0 &&
        this._cascadeSpawnCount < this._cascadeMaxSpawns &&
        this._cascadeWindowRemaining > 0
      ) {
        const wispCount = this.deps.summons.getWispCount();
        const maxWisps = this.deps.summons.getMaxWisps();
        if (wispCount < maxWisps) {
          const pos = this.deps.player.getPosition();
          this.deps.summons.spawnWisp(pos.x, pos.y);
          this._cascadeSpawnCount++;
          this._totalCascadeSpawns++;
        }
        // Advance interval regardless of whether the spawn was gated by maxWisps —
        // the next attempt comes after another full interval, not immediately.
        this._cascadeIntervalRemaining += this._cascadeIntervalMs;
      }

      if (this._cascadeWindowRemaining <= 0) {
        this._cascadeWindowRemaining = 0;
      }
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
    this._cascadeWindowRemaining = 0;
    this._cascadeIntervalRemaining = 0;
    this._cascadeSpawnCount = 0;
    this._cascadeMaxSpawns = 0;
    this._cascadeIntervalMs = 0;
  }
}
